import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { coaches as coachesApi, packages as packagesApi, upload as uploadApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { getSeasonRange } from "../utils/season";

const SKILL_OPTIONS = [
  { value: "beginner",     label: "初學者" },
  { value: "intermediate", label: "中級" },
  { value: "advanced",     label: "進階" },
];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("zh-TW", {
    year: "numeric", month: "long", day: "numeric", weekday: "short",
  });
}
function formatTime(t) { return t ? String(t).slice(0, 5) : ""; }

export default function CoachDashboard() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [tab, setTab]             = useState("profile");
  const [profile, setProfile]     = useState(null);
  const [bookings, setBookings]   = useState([]);
  const [stats, setStats]         = useState(null);
  const [leaves, setLeaves]       = useState([]);
  const [pkgList, setPkgList]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg]             = useState("");
  const [error, setError]         = useState("");

  // 新增請假表單
  const [newLeave, setNewLeave] = useState({ leave_date: "", package_id: "" });
  const seasonRange = getSeasonRange();

  useEffect(() => {
    if (!user || user.role !== "coach") { navigate("/"); return; }
    loadAll();
  }, [user]);

  async function loadAll() {
    setLoading(true);
    try {
      const [p, b, s, lv, pk] = await Promise.all([
        coachesApi.getMe(),
        coachesApi.getMyBookings(),
        coachesApi.getMyStats(),
        coachesApi.getMyLeaves(),
        packagesApi.list(),
      ]);
      setProfile(p);
      setBookings(b);
      setStats(s);
      setLeaves(lv);
      setPkgList(pk);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── 儲存個人資料 ──
  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      await coachesApi.updateMe({
        name:             profile.name,
        phone:            profile.phone,
        bio:              profile.bio,
        years_experience: Number(profile.years_experience),
        skill_levels:     profile.skill_levels,
        instagram:        profile.instagram,
        facebook:         profile.facebook,
        line_id:          profile.line_id,
        avatar_url:       profile.avatar_url,
      });
      setMsg("儲存成功！");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── 上傳照片 ──
  async function handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError("圖片大小不能超過 10MB"); return; }
    setUploading(true);
    try {
      const { url } = await uploadApi.avatar(file);
      setProfile(prev => ({ ...prev, avatar_url: url }));
      setMsg("照片上傳成功！");
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  // ── 新增請假 ──
  async function handleAddLeave(e) {
    e.preventDefault();
    if (!newLeave.leave_date) { setError("請選擇請假日期"); return; }
    try {
      const result = await coachesApi.addLeave({
        leave_date: newLeave.leave_date,
        package_id: newLeave.package_id ? Number(newLeave.package_id) : null,
      });
      setMsg("請假新增成功！");
      setNewLeave({ leave_date: "", package_id: "" });
      // 重新載入請假列表
      const lv = await coachesApi.getMyLeaves();
      setLeaves(lv);
    } catch (err) {
      setError(err.message);
    }
  }

  // ── 刪除請假 ──
  async function handleDeleteLeave(id) {
    if (!window.confirm("確定取消此請假？")) return;
    try {
      await coachesApi.deleteLeave(id);
      setLeaves(prev => prev.filter(l => l.id !== id));
      setMsg("請假已取消");
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      <span className="spinner" style={{ margin: "0 auto" }} />
    </div>
  );

  const tabs = [
    { key: "profile",  label: "個人資料" },
    { key: "leaves",   label: "請假管理" },
    { key: "bookings", label: "預約列表" },
    { key: "stats",    label: "統計" },
  ];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>

      <h1 style={{ fontSize: 36, marginBottom: 8 }}>教練後台</h1>
      <p style={{ color: "var(--muted)", marginBottom: 32 }}>管理你的個人資料、請假和預約</p>

      {msg && (
        <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "var(--green)", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 14 }}
          onClick={() => setMsg("")}>{msg}</div>
      )}
      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "var(--red)", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 14 }}
          onClick={() => setError("")}>{error}</div>
      )}

      {/* Tab 導覽 */}
      <div style={{ display: "flex", gap: 4, marginBottom: 32, borderBottom: "1px solid var(--stone)" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "10px 20px", fontSize: 15, fontWeight: 500,
              color: tab === t.key ? "var(--ink)" : "var(--muted)",
              borderBottom: tab === t.key ? "2px solid var(--ink)" : "2px solid transparent",
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── 個人資料 ── */}
      {tab === "profile" && profile && (
        <form onSubmit={handleSaveProfile} style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 600 }}>

          {/* 頭像 */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: "var(--stone)", overflow: "hidden", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {profile.avatar_url
                ? <img src={profile.avatar_url} alt="頭像" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: 28 }}>{profile.name?.[0]}</span>
              }
            </div>
            <div>
              <label style={{
                display: "inline-block", padding: "8px 16px",
                background: "var(--ink)", color: "var(--white)",
                borderRadius: 8, cursor: "pointer", fontSize: 14,
              }}>
                {uploading ? "上傳中..." : "更換照片"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarUpload} disabled={uploading} />
              </label>
              <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>JPG、PNG、WebP，最大 10MB</p>
            </div>
          </div>

          {/* 基本資料 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>姓名</label>
              <input className="input-field" value={profile.name || ""} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>電話</label>
              <input className="input-field" value={profile.phone || ""} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>自我介紹</label>
            <textarea className="input-field" rows={4} style={{ resize: "vertical" }}
              value={profile.bio || ""} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} />
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>教學年資</label>
            <input className="input-field" type="number" min="0" style={{ maxWidth: 160 }}
              value={profile.years_experience || 0}
              onChange={e => setProfile(p => ({ ...p, years_experience: e.target.value }))} />
          </div>

          {/* 教學程度 */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 8, color: "var(--ink2)" }}>教學程度</label>
            <div style={{ display: "flex", gap: 10 }}>
              {SKILL_OPTIONS.map(opt => {
                const selected = (profile.skill_levels || []).includes(opt.value);
                return (
                  <div key={opt.value} onClick={() => {
                    const current = profile.skill_levels || [];
                    setProfile(p => ({
                      ...p,
                      skill_levels: selected
                        ? current.filter(s => s !== opt.value)
                        : [...current, opt.value],
                    }));
                  }} style={{
                    padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 14,
                    background: selected ? "var(--ink)" : "var(--white)",
                    color: selected ? "var(--white)" : "var(--ink)",
                    border: `1.5px solid ${selected ? "var(--ink)" : "var(--stone)"}`,
                  }}>
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 社群帳號 */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 8, color: "var(--ink2)" }}>社群帳號</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input className="input-field" placeholder="Instagram 帳號（不含 @）"
                value={profile.instagram || ""} onChange={e => setProfile(p => ({ ...p, instagram: e.target.value }))} />
              <input className="input-field" placeholder="Facebook 帳號或連結"
                value={profile.facebook || ""} onChange={e => setProfile(p => ({ ...p, facebook: e.target.value }))} />
              <input className="input-field" placeholder="Line ID"
                value={profile.line_id || ""} onChange={e => setProfile(p => ({ ...p, line_id: e.target.value }))} />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ height: 48, alignSelf: "flex-start", minWidth: 120 }} disabled={saving}>
            {saving ? <span className="spinner" style={{ borderTopColor: "var(--white)" }} /> : "儲存變更"}
          </button>
        </form>
      )}

      {/* ── 請假管理 ── */}
      {tab === "leaves" && (
        <div>
          {/* 新增請假表單 */}
          <div className="card" style={{ padding: 24, marginBottom: 24 }}>
            <h3 style={{ fontSize: 18, marginBottom: 8 }}>新增請假</h3>
            <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
              不指定方案表示整天請假；指定方案則只請該時段的假。
            </p>
            <form onSubmit={handleAddLeave} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>請假日期</label>
                <input className="input-field" type="date" style={{ width: 180 }}
                  min={seasonRange.min}
                  max={seasonRange.max}
                  value={newLeave.leave_date}
                  onChange={e => setNewLeave(s => ({ ...s, leave_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>方案（選填，不選為整天）</label>
                <select className="input-field" style={{ width: 200 }}
                  value={newLeave.package_id}
                  onChange={e => setNewLeave(s => ({ ...s, package_id: e.target.value }))}>
                  <option value="">整天請假</option>
                  {pkgList.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name}（{formatTime(pkg.start_time)}–{formatTime(pkg.end_time)}）
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-primary" style={{ height: 46 }}>新增請假</button>
            </form>
          </div>

          {/* 請假列表 */}
          <h3 style={{ fontSize: 18, marginBottom: 16 }}>請假記錄（{leaves.length} 筆）</h3>
          {leaves.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
              目前沒有請假記錄
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {leaves.map(l => (
                <div key={l.id} className="card" style={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontWeight: 500 }}>{formatDate(l.leave_date)}</span>
                    <span style={{
                      marginLeft: 12, fontSize: 13,
                      color: l.package_id ? "var(--ink2)" : "var(--muted)",
                      background: l.package_id ? "var(--off)" : "transparent",
                      padding: l.package_id ? "2px 8px" : "0",
                      borderRadius: 6,
                    }}>
                      {l.package_name
                        ? `${l.package_name}（${formatTime(l.start_time)}–${formatTime(l.end_time)}）`
                        : "整天"}
                    </span>
                  </div>
                  <button className="btn btn-outline"
                    style={{ fontSize: 13, padding: "6px 14px", color: "var(--red)", borderColor: "#FECACA" }}
                    onClick={() => handleDeleteLeave(l.id)}>
                    取消請假
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 預約列表 ── */}
      {tab === "bookings" && (
        <div>
          <h3 style={{ fontSize: 18, marginBottom: 16 }}>所有預約（{bookings.length} 筆）</h3>
          {bookings.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>還沒有預約</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {bookings.map(b => (
                <div key={b.id} className="card" style={{ padding: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{b.student_name}</div>
                      <div style={{ fontSize: 13, color: "var(--muted)" }}>{b.student_email}</div>
                      {b.student_phone && <div style={{ fontSize: 13, color: "var(--muted)" }}>{b.student_phone}</div>}
                    </div>
                    <span className={`badge ${b.status === "confirmed" ? "badge-green" : b.status === "cancelled" ? "badge-gray" : "badge-blue"}`}>
                      {b.status === "pending" ? "待付款" : b.status === "confirmed" ? "已確認" : b.status === "cancelled" ? "已取消" : "已完成"}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: 14, background: "var(--off)", borderRadius: 8, fontSize: 14 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>日期</div>
                      <div style={{ fontWeight: 500 }}>{formatDate(b.date ? b.date : b.booking_date)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>時間</div>
                      <div style={{ fontWeight: 500 }}>{formatTime(b.start_time)} – {formatTime(b.end_time)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>費用</div>
                      <div style={{ fontWeight: 500 }}>NT$ {Number(b.total_price).toLocaleString()}</div>
                    </div>
                  </div>
                  {b.notes && <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 10 }}>備註：{b.notes}</p>}
                  {b.meeting_point && <p style={{ fontSize: 13, color: "var(--muted)" }}>集合：{b.meeting_point}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 統計 ── */}
      {tab === "stats" && stats && (
        <div>
          <h3 style={{ fontSize: 18, marginBottom: 20 }}>課堂與收入統計</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, maxWidth: 600 }}>
            {[
              { label: "已確認課堂", value: stats.total_bookings,     unit: "堂" },
              { label: "已完成課堂", value: stats.completed_bookings, unit: "堂" },
              { label: "預期收入",   value: `NT$ ${Number(stats.total_revenue).toLocaleString()}`,     unit: "" },
              { label: "已實現收入", value: `NT$ ${Number(stats.completed_revenue).toLocaleString()}`, unit: "" },
            ].map((item, i) => (
              <div key={i} className="card" style={{ padding: 24 }}>
                <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontSize: 28, fontFamily: "'DM Serif Display', serif" }}>
                  {item.value}{item.unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}